import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

//crop
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import 'react-image-crop/dist/ReactCrop.css'
import { useDebounceEffect } from "../../../../utils/imageCrop/useDebounceEffect";
import { canvasPreview } from "../../../../utils/imageCrop/canvasPreview";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// layouts
import FormField from "layouts/ecommerce/products/edit-product/components/FormField";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// redux
import { useSelector, useDispatch } from "react-redux";
import * as actions from "redux/actions/event";

//modal
import { Button, Modal } from "react-bootstrap";

// EventDto: {id, userId, title, description, eventDate, location, photoUrl}
// — photoUrl was added alongside Family/Item's; photo is optional.
const emptyForm = {
  id: 0,
  title: "",
  description: "",
  eventDate: "",
  location: "",
  photoUrl: "",
  photoFile: null,
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  )
}

const EventForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;

  const event = useSelector((state) => state.event);

  const [form, setForm] = React.useState(
    editData
      ? { ...emptyForm, ...editData, eventDate: toDateInputValue(editData.eventDate) }
      : emptyForm
  );
  const [imgCropPermission, setImgCropPermission] = React.useState(false)
  const [imgSrc, setImgSrc] = React.useState('')
  const previewCanvasRef = React.useRef(null)
  const imgRef = React.useRef(null)
  const [crop, setCrop] = React.useState()
  const [completedCrop, setCompletedCrop] = React.useState()
  const [scale, setScale] = React.useState(1)
  const [rotate, setRotate] = React.useState(0)
  const [aspect, setAspect] = React.useState(16 / 9)
  const [show, setShow] = React.useState(false);
  const [imgCropStatus, setImgCropStatus] = React.useState(false)

  React.useEffect(() => {
    if (event.eventSaved) navigate(`/upload/event`);
  }, [event, navigate]);

  const handleClose = () => {
    setImgCropStatus(false)
    setImgCropPermission(false)
    setShow(false);
  }

  const handleShow = () => setShow(true);

  const canvasToBlob = (canvas) =>
    new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92));

  const handleConfirm = async () => {
    const fileSizeMb = imgSrc.length / 1024 / 1024;
    if (fileSizeMb > 15) {
      alert('File can not bigger than 15mb.')
    } else if (imgCropStatus && imgCropPermission && previewCanvasRef.current) {
      const blob = await canvasToBlob(previewCanvasRef.current);
      setForm(prev => ({ ...prev, photoFile: blob }));
    } else {
      setForm(prev => ({ ...prev, photoFile: form._rawFile }));
    }
    setImgCropStatus(false)
    setImgCropPermission(false)
    setShow(false);
  }

  const handleCropPhoto = async () => {
    canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoto = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      handleShow()
      setCrop(undefined)
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImgSrc(reader.result.toString() || '')
        setForm(prev => ({ ...prev, _rawFile: file }));
      })
      reader.readAsDataURL(file)
    }
  };

  const handleSubmit = () => {
    if (!form.title) {
      alert("Title is required.");
      return;
    }
    const payload = {
      ...form,
      eventDate: form.eventDate ? new Date(form.eventDate).toISOString() : new Date().toISOString(),
    };
    if (editData) dispatch(actions.editEvent(payload));
    else dispatch(actions.addEvent(payload));
  };

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget
      setCrop(centerAspectCrop(width, height, aspect))
    }
  }

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate)
      }
    },
    100,
    [completedCrop, scale, rotate],
  )

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Grid container>
        <Grid item xs={12} md={8} xl={6}>
          <MDBox py={3} pr={1}>
            <Card sx={{ width: "100%" }}>
              <MDBox display="flex">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="4rem"
                  height="4rem"
                  variant="gradient"
                  bgColor="primary"
                  color="white"
                  shadow="md"
                  borderRadius="xl"
                  ml={3}
                  mt={-2}
                >
                  <Icon fontSize="medium" color="inherit">
                    add
                  </Icon>
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    {editData ? "Edit Event" : "Add Event"}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type="text"
                      label="Title"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type="text"
                      label="Location"
                      name="location"
                      value={form.location}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      type="date"
                      label="Event Date"
                      name="eventDate"
                      value={form.eventDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormField
                      type="text"
                      label="Description"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      multiline
                      rows={4}
                    />
                  </Grid>
                </Grid>

                <MDBox mt={2}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <MDButton color="secondary">
                        <input
                          type="file"
                          label="Photo"
                          name="photo"
                          accept="image/*"
                          onChange={handlePhoto}
                        />
                      </MDButton>
                    </Grid>
                    <>
                      <Modal show={show} centered>
                        {imgCropPermission && <strong className="text-center m-3">Note : To crop image move the crop window</strong>}
                        <Modal.Body>
                          {Boolean(imgSrc) && imgCropPermission ? (
                            <ReactCrop
                              crop={crop}
                              onChange={(_, percentCrop) => {
                                setCrop(percentCrop)
                                setImgCropStatus(true)
                              }}
                              onComplete={(c) => {
                                setCompletedCrop(c)
                                setTimeout(() => { handleCropPhoto() }, 1000)
                              }}
                              aspect={aspect}
                            >
                              <img
                                className="text-center"
                                ref={imgRef}
                                alt="Crop"
                                src={imgSrc}
                                style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                                onLoad={onImageLoad}
                              />
                            </ReactCrop>
                          ) :
                            <div className="w-100">
                              <img ref={imgRef} alt="Preview" src={imgSrc} style={{ width: "100%" }} />
                            </div>
                          }
                          <div className="text-center" style={{ display: "none" }}>
                            {Boolean(completedCrop) && (
                              <canvas
                                ref={previewCanvasRef}
                                style={{
                                  border: '1px solid black',
                                  objectFit: 'contain',
                                  width: completedCrop.width,
                                  height: completedCrop.height,
                                }}
                              />
                            )}
                          </div>
                        </Modal.Body>
                        <Modal.Footer>
                          <Button variant="secondary" onClick={handleClose}>Close</Button>
                          {!imgCropPermission && (
                            <Button variant="success" onClick={() => setImgCropPermission(!imgCropPermission)}>
                              Crop image
                            </Button>
                          )}
                          <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
                        </Modal.Footer>
                      </Modal>
                    </>
                  </Grid>
                </MDBox>

                <MDBox mt={3} display="flex" justifyContent="end">
                  <MDButton color="primary" onClick={!event.savingEvent ? handleSubmit : undefined}>
                    {event.savingEvent ? "Submitting..." : "Submit"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default EventForm;
